// @TODO: Grab these values from CARES API exposed via fnGetAssessmentRate()
export const NON_PARTICIPATING_FEE = 300;
export const PARTICIPATING_FEE = 6200;

export interface ParticipationFields {
  participating: boolean;
  participationLocked: boolean;
  baseBalance: number;
  balance: string;
  amount: string | number;
}

export interface ApiBalanceInput {
  amountDue: string | number;
  participating: boolean;
}

const toNumber = (value: string | number): number => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(String(value).replace(/[^0-9.-]+/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatAmount = (value: number): string => value.toFixed(2);

export const fromApiBalance = (input: ApiBalanceInput): ParticipationFields => {
  const baseBalance = toNumber(input.amountDue);
  const participating = Boolean(input.participating);
  return {
    participating,
    participationLocked: participating,
    baseBalance,
    balance: formatAmount(baseBalance),
    amount: formatAmount(baseBalance),
  };
};

export const toggleParticipate = <T extends ParticipationFields>(item: T): T => {
  if (item.participationLocked) {
    return item;
  }

  if (item.participating) {
    const restored = item.baseBalance > 0 ? item.baseBalance : NON_PARTICIPATING_FEE;
    return {
      ...item,
      participating: false,
      balance: formatAmount(restored),
      amount: formatAmount(restored),
    };
  }

  return {
    ...item,
    participating: true,
    balance: formatAmount(PARTICIPATING_FEE),
    amount: formatAmount(PARTICIPATING_FEE),
  };
};

/** Payment amount is editable for participating assessments with a balance due greater than $300. */
export const canEditPaymentAmount = (item: Pick<ParticipationFields, 'participating' | 'balance'>): boolean => {
  if (!item.participating) return false;
  return toNumber(item.balance) > NON_PARTICIPATING_FEE;
};

export const isValidPaymentAmount = (
  amount: string | number,
  item: Pick<ParticipationFields, 'participating' | 'participationLocked' | 'balance'>,
): boolean => {
  const parsed = toNumber(amount);
  if (parsed <= 0) return false;
  if (canEditPaymentAmount(item) && parsed <= NON_PARTICIPATING_FEE) return false;
  return true;
};

export const formatToUSD = (amount: string | number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(amount));

export const calculateTotal = (values: Array<{ amount: string | number }>): string =>
  formatAmount(
    values.reduce((partialSum, item) => {
      if (typeof item.amount === 'string' && item.amount === '') {
        return partialSum;
      }
      return partialSum + toNumber(item.amount);
    }, 0),
  );
